/* eslint-disable max-lines */
import { z } from "zod";
import { PaginationSchema } from "@/components/paginate/types";
import {
  SubscriptionType,
  SubscriptionStatus,
  RunStatus,
  RuleSource,
  PlaygroundReportContentScope,
  VariableType,
  ChannelType,
  RuleVisibility,
} from "@/pages/subscriptions/constants";

/** Backend sends ``team_id`` as a number (or null on private/legacy rules);
 *  ``UserGroup.id`` is a string, so normalise here rather than at every
 *  lookup site. */
const TeamIdSchema = z
  .union([z.string(), z.number()])
  .nullish()
  .transform(value => (value === null || value === undefined ? null : String(value)));

/** Rules created before team sharing shipped omit the field — read as private. */
const VisibilitySchema = z.nativeEnum(RuleVisibility).default(RuleVisibility.PRIVATE);

/** Per-caller permission flags, computed server-side. Never derive these from
 *  ``created_by``: only the backend knows whether the caller is a tenant owner.
 *  Defaults cover rules that predate the flags. */
const PermissionFlagsShape = {
  can_edit: z.boolean().default(true),
  can_delete: z.boolean().default(false),
  can_share: z.boolean().default(false),
};

// Shared Zod schemas for API validation
export const SubscriptionV2VariableSchema = z.object({
  id: z.string(),
  variableId: z.string().optional(), // Keep a copy of id that won't be overwritten by useFieldArray
  type: z.nativeEnum(VariableType),
  value: z.string(),
  displayValue: z.string(),
  config: z
    .object({
      options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
      isEditable: z.boolean().optional(),
      multiSelect: z.boolean().optional(),
      category: z.string().optional(), // Category identifier for entity variables
    })
    .optional(),
  // New fields for prompt generation
  fromPreviousStep: z.boolean().optional(),
  tooltip: z.string().optional(),
  filterCount: z.number().optional(),
  isPlaceholder: z.boolean().optional(), // Indicates if variable needs user input
});

export const NotifierConfigSchema = z.object({
  type: z.nativeEnum(ChannelType),
  recipients: z.array(z.string()).nullish(), // For email; backend sends null for slack notifiers
  channel: z.string().nullish(), // For slack; backend sends null for email notifiers
});

export const ScheduleConfigSchema = z.object({
  cron: z.string(),
  timezone: z.string().default("UTC"),
});

// Delivery config: combines schedule + notifiers in new API
export const DeliverySchema = z.object({
  schedule: ScheduleConfigSchema.nullish(), // Backend may send null; uses default cron
  notifiers: z.array(NotifierConfigSchema).default([]),
});

export const RawSQLMetricConfigSchema = z.object({
  db_type: z.string().optional(),
  context_query: z.string().optional(),
  trigger_query: z.string().optional(),
});

export const MetricConfigSchema = z.object({
  metric_name: z.string(),
  category: z.string().optional(), // Category key (e.g., "warehouse") - sent in create/update requests
  params: z.object({
    comparison_type: z.string().optional(),
    threshold_type: z.string().optional(),
    threshold_value: z.number().optional(),
    is_percentage: z.boolean().nullable().optional(),
    date_range: z.string().optional(),
    aggregation: z.string().optional(),
  }),
  filters: z
    .array(
      z.object({
        order: z.number(),
        key: z.string(),
        value: z.union([z.array(z.string()), z.string()]), // Support both array and string formats
      })
    )
    .optional(), // Filters array
});

// Template schema - matches _template_to_dict() in the backend
export const SubscriptionV2TemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  platform: z.string().optional(),
  rule_type: z.nativeEnum(SubscriptionType),
  entity_type: z.string(),
  metric_config: z.object({
    category: z.string(),
    metric_name: z.string(),
    params: z.object({
      aggregation: z.string().optional(),
      date_range: z.string().optional(),
      comparison_type: z.string().optional(),
      threshold_type: z.string().optional(),
      threshold_value: z.number().optional(),
      is_percentage: z.boolean().nullable().optional(),
    }),
    filters: z.array(z.record(z.unknown())).optional(),
  }),
  delivery: DeliverySchema,
  alert_message: z.string().optional(),
  tags: z.array(z.string()).optional(),
  group: z
    .object({
      key: z.string(),
      label: z.string(),
      description: z.string().optional(),
      icon: z.string().optional(),
    })
    .optional(),
});

export const SubscriptionTemplatesListResponseSchema = z.object({
  items: z.array(SubscriptionV2TemplateSchema),
  count: z.number(),
});

// Last run from backend (full run detail with result)
const LastRunSchema = z
  .object({
    id: z.number(),
    rule_id: z.number().optional(),
    status: z.nativeEnum(RunStatus),
    result: z.record(z.unknown()).nullable().optional(),
    error: z.unknown().nullable().optional(),
    message: z.string().nullable().optional(),
    executed_at: z.string(),
    created_at: z.string().optional(),
  })
  .nullable();

// Single recent run from backend (id, status, executed_at, message)
// Backend can omit message for skipped runs.
export const RecentRunSchema = z.object({
  id: z.number(),
  status: z.nativeEnum(RunStatus),
  executed_at: z.string(),
  message: z
    .string()
    .nullish()
    .transform(value => value ?? null),
});

// List page schemas
export const SubscriptionV2ListItemSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  rule_type: z.nativeEnum(SubscriptionType),
  // Same discriminator as the detail response. Old backends omit it —
  // default to ``metric`` so existing rows keep their behaviour.
  source: z.nativeEnum(RuleSource).default(RuleSource.METRIC),
  // Null on playground_report rows, which carry no metric config at all.
  platform: z.string().nullish().default(""),
  entity_type: z.string().nullish().default(""),
  category: z.string().nullish().default(""),
  summary: z.string().optional().nullable(),
  last_run: LastRunSchema.optional().default(null),
  recent_runs: z.array(RecentRunSchema).optional().default([]),
  delivery: DeliverySchema,
  metric_config: MetricConfigSchema.or(RawSQLMetricConfigSchema).nullish(),
  alert_message: z.string().nullable().optional(),
  created_by: z.number(),
  created_by_name: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().optional(),
  status: z.nativeEnum(SubscriptionStatus),
  read_only: z.boolean().default(false),
  visibility: VisibilitySchema,
  team_id: TeamIdSchema,
  ...PermissionFlagsShape,
});

export const SubscriptionV2SummarySchema = z.object({
  total: z.number(),
  active: z.number(),
  alerts: z.number(),
  reports: z.number(),
});

export const SubscriptionListResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(SubscriptionV2ListItemSchema),
});

// Filter option schema
export const FilterOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

// Per-run recipient snapshot (server-side ``RunResponse.destinations``).
// Populated for playground_report runs; may be null for metric alerts
// that don't snapshot delivery yet.
export const RunDestinationsSchema = z.object({
  emails: z.array(z.string()).default([]),
  slack_webhook_id: z.number().nullable().optional(),
});
export type RunDestinations = z.infer<typeof RunDestinationsSchema>;

/** The retry counter from ``result.deferred`` on a DEFERRED run. The payload
 *  also carries table/reason/watermark/window_end, left out until something
 *  renders them — the human-readable form of all four is already in
 *  ``message``. ``result`` stays a loose record (a passthrough object would
 *  fire the unknown-field warning for every other status), so callers
 *  ``safeParse`` this against ``item.result?.deferred``. */
export const DeferredRunDetailsSchema = z.object({
  attempt: z.number(),
  max_attempts: z.number(),
});
export type DeferredRunDetails = z.infer<typeof DeferredRunDetailsSchema>;

export const SubscriptionHistoryItemSchema = z.object({
  id: z.coerce.string(),
  rule_id: z.number().optional(),
  status: z.nativeEnum(RunStatus),
  executed_at: z.string(), // ISO 8601 format (renamed from triggered_at)
  created_at: z.string().optional(),
  // Backend can omit/null this for skipped alert runs; normalize to "" so
  // downstream renderers can treat it as a plain string.
  message: z
    .string()
    .nullish()
    .transform(value => value ?? ""),
  error: z
    .object({
      message: z.string().optional(),
    })
    .nullable()
    .optional(),
  result: z.record(z.unknown()).nullable().optional(),
  // Per-run recipient snapshot — required for the History table's
  // Destination column on subscription-detail. Not populated for
  // metric alerts today, so nullable.
  destinations: RunDestinationsSchema.nullable().optional(),
});

export const SubscriptionHistoryResponseSchema = z.object({
  items: z.array(SubscriptionHistoryItemSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
});

export const SubscriptionDestinationConfigSchema = z.object({
  channels: z.array(z.nativeEnum(ChannelType)),
  email_recipients: z.array(z.string()),
  slack_channels: z.array(z.string()),
});

export const SubscriptionDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  prompt: z.string(),
  variables: z.array(SubscriptionV2VariableSchema),
  status: z.enum([SubscriptionStatus.ACTIVE, SubscriptionStatus.INACTIVE]),
  rule_type: z.nativeEnum(SubscriptionType),
  category: z.string(), // e.g., "warehouse", "user", "table", "dashboard"
  source: z.string(), // Data source identifier (e.g., "snowflake-1", "dbt-1", "tableau-1")
  schedule: z.string(),
  destination_config: SubscriptionDestinationConfigSchema,
  created_by: z.string(),
  created_at: z.string(), // ISO 8601
  updated_at: z.string(), // ISO 8601
  history: SubscriptionHistoryResponseSchema,
});

// Frontend detail view type (transformed from backend response)
// This includes variables and prompt which are generated from backend's structured data
export const SubscriptionDetailViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  rule_type: z.nativeEnum(SubscriptionType),
  // Discriminator so downstream consumers (detail page, header) can
  // branch between classic metric rules and playground_report rules.
  source: z.nativeEnum(RuleSource).default(RuleSource.METRIC),
  // Populated for source=playground_report. The Studio session that
  // owns this recurring report — used to fetch the underlying prompt
  // for the Description section and to deep-link back into Studio.
  session_id: z.string().nullable().optional(),
  content_scope: z.nativeEnum(PlaygroundReportContentScope).nullable().optional(),
  platform: z.string().optional(),
  template_id: z.string().optional(),
  prompt: z.string(),
  description: z.string().optional().nullable(),
  variables: z.array(SubscriptionV2VariableSchema),
  destination_config: SubscriptionDestinationConfigSchema,
  status: z.enum([SubscriptionStatus.ACTIVE, SubscriptionStatus.INACTIVE]),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string(),
  next_execution_at: z.string().optional(),
  read_only: z.boolean().default(false),
});

// Type definitions inferred from Zod schemas
export type SubscriptionV2Variable = z.infer<typeof SubscriptionV2VariableSchema>;
export type SubscriptionV2Template = z.infer<typeof SubscriptionV2TemplateSchema>;
export type SubscriptionTemplatesListResponse = z.infer<
  typeof SubscriptionTemplatesListResponseSchema
>;
export type RecentRun = z.infer<typeof RecentRunSchema>;
export type SubscriptionV2ListItem = z.infer<typeof SubscriptionV2ListItemSchema>;
export type SubscriptionV2Summary = z.infer<typeof SubscriptionV2SummarySchema>;
export type SubscriptionListResponse = z.infer<typeof SubscriptionListResponseSchema>;
export type FilterOption = z.infer<typeof FilterOptionSchema>;

// Extended list item with display name lookups
export type EnrichedSubscriptionListItem = SubscriptionV2ListItem & {
  categoryDisplayName?: string;
  /** Always resolved by ``useSubscriptionData`` — name, else email, else the
   *  literal "Unknown". Non-optional so consumers don't add a dead fallback. */
  createdByName: string;
};

export type SubscriptionHistoryItem = z.infer<typeof SubscriptionHistoryItemSchema>;
export type SubscriptionHistoryResponse = z.infer<typeof SubscriptionHistoryResponseSchema>;
export type SubscriptionDetail = z.infer<typeof SubscriptionDetailSchema>;
export type SubscriptionDetailView = z.infer<typeof SubscriptionDetailViewSchema>;

// Form validation schemas
export const FilterSchema = z.object({
  dimension: z.string().min(1, "Dimension is required"),
  value: z.array(z.string()).min(1, "At least one value is required"),
});

// Email validation helper
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SubscriptionFormSchema = z
  .object({
    name: z
      .string()
      .min(1, "Subscription name is required")
      .max(255, "Subscription name must be 255 characters or less"),
    rule_type: z.nativeEnum(SubscriptionType, {
      required_error: "Subscription type is required",
    }),
    platform: z.string().optional(),
    source: z.string().min(1, "Source is required"),
    source_name: z.string().optional(), // The actual source name (e.g., "snowflake")
    metric: z.string().min(1, "Metric is required"),
    filters: z.array(FilterSchema).default([]),
    // Fields for CustomizeSubscriptionStep
    prompt: z.string().optional(),
    variables: z.array(SubscriptionV2VariableSchema).optional(),
    // Fields for DestinationStep
    channels: z
      .array(z.nativeEnum(ChannelType))
      .min(1, "At least one channel (Email or Slack) must be selected")
      .default([]),
    email_recipients: z.array(z.string()).default([]),
    slack_channels: z.array(z.string()).default([]),
    visibility: VisibilitySchema,
  })
  .refine(
    data => {
      // If email channel is selected, at least one valid email is required
      if (data.channels.includes(ChannelType.EMAIL)) {
        return (
          data.email_recipients.length > 0 &&
          data.email_recipients.every(email => emailRegex.test(email.trim()))
        );
      }
      return true;
    },
    {
      message: "At least one valid email address is required when Email channel is selected",
      path: ["email_recipients"],
    }
  )
  .refine(
    data => {
      // If slack channel is selected, at least one slack channel is required
      if (data.channels.includes(ChannelType.SLACK)) {
        return data.slack_channels.length > 0;
      }
      return true;
    },
    {
      message: "At least one Slack channel is required when Slack channel is selected",
      path: ["slack_channels"],
    }
  );

export type FilterFormData = z.infer<typeof FilterSchema>;
export type SubscriptionFormData = z.infer<typeof SubscriptionFormSchema>;

export const BackendSubscriptionCreateRequestSchema = z.object({
  name: z.string(),
  rule_type: z.nativeEnum(SubscriptionType),
  platform: z.string().optional(),
  entity_type: z.string(),
  metric_config: MetricConfigSchema,
  delivery: DeliverySchema,
  description: z.string().optional(),
  alert_message: z.string().optional(),
  status: z
    .enum([SubscriptionStatus.ACTIVE, SubscriptionStatus.INACTIVE])
    .default(SubscriptionStatus.ACTIVE),
  // Never send ``team_id`` — it is not accepted on the request body and is
  // silently dropped. The team comes from the ``x-user-group-id`` header.
  visibility: z.nativeEnum(RuleVisibility).optional(),
});

// Polymorphic rule response. Discriminated by ``source``:
//   - "metric" (default, backwards-compatible) uses ``metric_config`` /
//     ``platform`` / ``entity_type``; ``session_id`` and ``content_scope``
//     are null.
//   - "playground_report" is a recurring Studio chat report; the metric
//     fields are null and ``session_id`` / ``content_scope`` are set.
// Fields the two shapes disagree on are widened to ``.nullable()`` here
// so a single schema validates both — consumers narrow with ``source``.
export const SubscriptionResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  rule_type: z.nativeEnum(SubscriptionType),
  // ``source`` is a new server-side discriminator. Old backends omit it —
  // treat the missing case as ``metric`` so we don't break existing rows.
  source: z.nativeEnum(RuleSource).default(RuleSource.METRIC),
  platform: z.string().nullable().optional(),
  entity_type: z.string().nullable().optional(),
  category: z.string().optional(),
  summary: z.string().optional().nullable(),
  // Null for playground_report rules; present for metric rules.
  metric_config: MetricConfigSchema.or(RawSQLMetricConfigSchema).nullable().optional(),
  // Populated for playground_report rules only.
  session_id: z.string().nullable().optional(),
  content_scope: z.nativeEnum(PlaygroundReportContentScope).nullable().optional(),
  delivery: DeliverySchema,
  last_run: LastRunSchema.optional().default(null),
  recent_runs: z.array(RecentRunSchema).optional().default([]),
  alert_message: z.string().nullable(),
  status: z.enum([SubscriptionStatus.ACTIVE, SubscriptionStatus.INACTIVE]),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.number(),
  read_only: z.boolean().default(false),
  visibility: VisibilitySchema,
  team_id: TeamIdSchema,
  ...PermissionFlagsShape,
});

// Playground-report create/update body. Hits the same
// POST /api/v1/alerts/rules endpoint as metric rules but with a
// different shape (no metric_config/platform/entity_type).
export const PlaygroundReportCreateRequestSchema = z.object({
  name: z.string(),
  source: z.literal(RuleSource.PLAYGROUND_REPORT),
  session_id: z.string(),
  content_scope: z.nativeEnum(PlaygroundReportContentScope),
  delivery: DeliverySchema,
  status: z
    .enum([SubscriptionStatus.ACTIVE, SubscriptionStatus.INACTIVE])
    .default(SubscriptionStatus.ACTIVE)
    .optional(),
});
export type PlaygroundReportCreateRequest = z.infer<typeof PlaygroundReportCreateRequestSchema>;

// Update body for POST /api/v1/alerts/rules/{id}. Identical to create
// minus ``source``, which is immutable after create and absent from the
// server's update schema. The server uses ``exclude_unset``, so every
// field here is optional — but ``delivery`` replaces wholesale, so a
// partial notifiers array silently drops the omitted destinations.
export const PlaygroundReportUpdateRequestSchema = PlaygroundReportCreateRequestSchema.omit({
  source: true,
}).partial();
export type PlaygroundReportUpdateRequest = z.infer<typeof PlaygroundReportUpdateRequestSchema>;

export const PlaygroundReportPreviewRequestSchema = z.object({
  session_id: z.string(),
  content_scope: z.nativeEnum(PlaygroundReportContentScope),
  report_name: z.string().optional(),
});
export type PlaygroundReportPreviewRequest = z.infer<typeof PlaygroundReportPreviewRequestSchema>;

// Background preview run on a Studio session. The alerts worker generates
// the report exactly as a scheduled run would, keeps it on the session and
// emails it to the person who started it. A run whose worker stopped
// arrives as ``failed``.
export const PlaygroundReportRunState = {
  QUEUED: "queued",
  RUNNING: "running",
  DONE: "done",
  FAILED: "failed",
} as const;

export const PlaygroundReportRunSchema = z.object({
  id: z.string(),
  kind: z.string(),
  state: z.string(),
  started_at: z.string(),
  finished_at: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  report_name: z.string().nullable().optional(),
  content_scope: z.string().nullable().optional(),
  emailed_to: z.string().nullable().optional(),
  email_error: z.string().nullable().optional(),
  // The run released the session before its requester email was sent; the
  // email's outcome is still to come.
  email_pending: z.boolean().optional(),
});
export type PlaygroundReportRun = z.infer<typeof PlaygroundReportRunSchema>;

// The latest generated preview kept on the session, sendable as is.
export const PlaygroundReportKeptPreviewSchema = z.object({
  run_id: z.string(),
  report_name: z.string(),
  generated_at: z.string(),
  subject: z.string(),
  content: z.string(), // markdown
  is_empty: z.boolean(),
});
export type PlaygroundReportKeptPreview = z.infer<typeof PlaygroundReportKeptPreviewSchema>;

export const PlaygroundReportRunStatusSchema = z.object({
  session_id: z.string(),
  run: PlaygroundReportRunSchema.nullable().optional(),
  preview: PlaygroundReportKeptPreviewSchema.nullable().optional(),
});
export type PlaygroundReportRunStatus = z.infer<typeof PlaygroundReportRunStatusSchema>;

export type BackendMetricConfig = z.infer<typeof MetricConfigSchema>;
export type RawSQLMetricConfig = z.infer<typeof RawSQLMetricConfigSchema>;
export type BackendScheduleConfig = z.infer<typeof ScheduleConfigSchema>;
export type BackendNotifierConfig = z.infer<typeof NotifierConfigSchema>;
export type BackendDelivery = z.infer<typeof DeliverySchema>;
export type BackendSubscriptionCreateRequest = z.infer<
  typeof BackendSubscriptionCreateRequestSchema
>;
export type SubscriptionResponse = z.infer<typeof SubscriptionResponseSchema>;

// Notification test endpoint: POST /api/v1/alerts/notifications/test
// Same shape as the create-rule body minus delivery, plus a single notifier.
export const TestNotifierSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(ChannelType.EMAIL),
    recipients: z.array(z.string()).min(1),
  }),
  z.object({
    type: z.literal(ChannelType.SLACK),
    channel: z.string().min(1),
  }),
]);

export const NotificationTestRequestSchema = z.object({
  name: z.string(),
  rule_type: z.nativeEnum(SubscriptionType).default(SubscriptionType.ALERT),
  platform: z.string().optional(),
  entity_type: z.string(),
  metric_config: MetricConfigSchema,
  alert_message: z.string().optional(),
  notifier: TestNotifierSchema,
  reference_time: z.string().optional(),
});

export const NotificationTestResponseSchema = z.object({
  ok: z.boolean(),
  target: z.enum([ChannelType.EMAIL, ChannelType.SLACK]),
  // Backend artifact (presigned URL) — accepted for schema compatibility but not surfaced in UI.
  chart_url: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});

// Delivery result of sending a kept playground-report preview
// (POST /api/v1/alerts/preview/playground-report/runs/{run_id}/send).
// Per-destination outcome. ``ok`` at the top level is true only when
// every destination succeeded, so a partial result (one delivered, one
// failed) arrives as ok:false with a mixed ``results`` array.
export const PlaygroundReportTestResultSchema = z.object({
  target: z.enum([ChannelType.EMAIL, ChannelType.SLACK]),
  ok: z.boolean(),
  error: z.string().nullable().optional(),
});

export const PlaygroundReportTestResponseSchema = z.object({
  ok: z.boolean(),
  results: z.array(PlaygroundReportTestResultSchema).default([]),
});

// Send the kept preview of a run, exactly as reviewed, with no new
// generation. Same per-destination response as the test endpoint.
export const PlaygroundReportSendPreviewRequestSchema = z.object({
  session_id: z.string(),
  notifiers: z.array(TestNotifierSchema).min(1),
});
export type PlaygroundReportSendPreviewRequest = z.infer<
  typeof PlaygroundReportSendPreviewRequestSchema
>;

export type TestNotifier = z.infer<typeof TestNotifierSchema>;
export type NotificationTestRequest = z.infer<typeof NotificationTestRequestSchema>;
export type NotificationTestResponse = z.infer<typeof NotificationTestResponseSchema>;
export type PlaygroundReportTestResult = z.infer<typeof PlaygroundReportTestResultSchema>;
export type PlaygroundReportTestResponse = z.infer<typeof PlaygroundReportTestResponseSchema>;

// Metric definition schema (internal normalized type)
export const MetricDefinitionSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  description: z.string(),
  category: z.string(),
  category_display_name: z.string(),
  source_name: z.string().optional(),
  source_display_label: z.string().optional(),
  entity_type: z.string(),
  unit: z.string(),
  supports_comparison_types: z.array(z.string()),
  supports_threshold_types: z.array(z.string()),
  supports_date_ranges: z.array(z.string()),
  supports_aggregations: z.array(z.string()),
  supported_filters: z.array(z.string()),
  searchable_filters: z.array(z.string()).default([]),
  filter_labels: z.record(z.string()).default({}),
  supported_subscription_types: z
    .array(z.nativeEnum(SubscriptionType))
    .default([SubscriptionType.REPORT, SubscriptionType.ALERT]),
  default_comparison_type: z.string(),
  default_threshold_type: z.string(),
  default_threshold: z.string(),
  default_date_range: z.string(),
  default_aggregation: z.string(),
});

export const CatalogPlatformSchema = z.enum(["snowflake", "databricks"]);
export type CatalogPlatform = z.infer<typeof CatalogPlatformSchema>;

// Metrics catalog response (actual backend API shape from /api/v1/alerts/metrics/catalog)
const CatalogMetricItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  unit: z.string(),
  aggregations: z.array(z.string()),
  supported_date_ranges: z.array(z.string()).optional(),
  source_name: CatalogPlatformSchema.optional(),
  source_display_label: z.string().optional(),
  // Per-metric subset of the entity's filters this metric's source can honour.
  supported_filters: z.array(z.string()).optional(),
});

const CatalogMetricGroupSchema = z.object({
  label: z.string(),
  metrics: z.array(CatalogMetricItemSchema),
});

const CatalogFilterSchema = z.object({
  key: z.string(),
  label: z.string(),
  column: z.string().optional(),
  type: z.string().optional(),
  options_url: z.string().optional(),
  searchable: z.boolean().optional(),
});

const CatalogEntityTypeSchema = z.object({
  key: z.string(),
  label: z.string(),
  icon: z.string().optional(),
  platform: CatalogPlatformSchema.optional(),
});

const CatalogEntitySchema = z.object({
  groups: z.array(CatalogMetricGroupSchema),
  filters: z.array(CatalogFilterSchema).optional(),
});

const CatalogOptionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  friendly: z.string().optional(),
  comparison_types: z.array(z.string()).optional(),
  cron: z.string().optional(),
});

export const MetricsCatalogResponseSchema = z.object({
  entity_types: z.array(CatalogEntityTypeSchema),
  metrics: z.record(CatalogEntitySchema),
  comparison_types: z.array(CatalogOptionSchema),
  threshold_types: z.array(CatalogOptionSchema),
  aggregations: z.array(CatalogOptionSchema),
  date_ranges: z.array(CatalogOptionSchema),
  frequencies: z.array(CatalogOptionSchema).optional(),
});

// Flat metrics response (legacy format, still accepted)
export const SubscriptionMetricsResponseSchema = z.array(MetricDefinitionSchema);

export type MetricDefinition = z.infer<typeof MetricDefinitionSchema>;
export type SubscriptionMetricsResponse = z.infer<typeof SubscriptionMetricsResponseSchema>;
export type MetricsCatalogResponse = z.infer<typeof MetricsCatalogResponseSchema>;

// Subscription Delete Response Schema
export const SubscriptionDeleteResponseSchema = z.object({
  ok: z.boolean(),
});

export type SubscriptionDeleteResponse = z.infer<typeof SubscriptionDeleteResponseSchema>;

// Metric Filter Values Response Schema (for legacy /subscriptions/metric-filter-options endpoint)
export const MetricFilterValuesResponseSchema = z.object({
  values: z.array(z.string()),
});

export type MetricFilterValuesResponse = z.infer<typeof MetricFilterValuesResponseSchema>;

// Discriminator literal for the 422 detail payload raised when a
// recipient's domain is not on the per-tenant allowlist. Consumed by
// `handleEmailDomainSaveError` to pin the message inline on the
// recipient form field.
export const EMAIL_DOMAIN_NOT_PERMITTED = "EMAIL_DOMAIN_NOT_PERMITTED" as const;
